'use client';

import { useId, useMemo, useRef } from 'react';

import { token } from '@flipster/styled-system/tokens';

import { useSvgChartDimensions } from '../chart/use-svg-chart-dimensions';

interface SimpleLinePulseChartProps {
  data: number[];
  width?: number;
  height?: number;
  type: 'mini';
  colors?: {
    start: string;
    end: string;
  };
}

export const SimpleLinePulseChart = ({ data, width, height, type, colors }: SimpleLinePulseChartProps) => {
  const gradientId = `lineGradient${useId()}`; // svg def id 충돌을 방지하기 위함

  const containerRef = useRef<SVGSVGElement>(null);
  const dimensions = useSvgChartDimensions(containerRef, width, height);

  const PADDING = CHART_CONSTANTS.PADDING.MINI;
  const POINT_SIZE = CHART_CONSTANTS.POINT.SIZE.MINI;
  const INNER_RADIUS = CHART_CONSTANTS.POINT.INNER_RADIUS.MINI;

  const chartData = useChartData(data, dimensions, type, PADDING, CHART_CONSTANTS.TEXT_MARGIN);
  const pathData = useMemo(() => {
    if (!chartData.length) return '';

    // 곡선 경로 생성
    let path = `M ${chartData[0].x},${chartData[0].y}`;

    // 각 점 사이에 곡선 추가
    for (let i = 0; i < chartData.length - 1; i++) {
      const current = chartData[i];
      const next = chartData[i + 1];

      // 제어점 계산 (큐빅 베지어 곡선)
      const sharpness = 3;
      const offset = (next.x - current.x) / sharpness;
      const controlX1 = current.x + offset;
      const controlX2 = next.x - offset;
      path += ` C ${controlX1},${current.y} ${controlX2},${next.y} ${next.x},${next.y}`;
    }

    return path;
  }, [chartData]);

  const activeIndex = data.length - 1;
  const activePoint = chartData[activeIndex];

  if (!chartData.length) {
    return <svg ref={containerRef} width={width || '100%'} height={height || '100%'} />;
  }

  return (
    <svg ref={containerRef} width={width || '100%'} height={height || '100%'} style={{ display: 'block' }}>
      <defs>
        <linearGradient id={gradientId} x1='0%' y1='50%' x2='100%' y2='50%' gradientUnits='userSpaceOnUse'>
          <stop offset='0%' stopColor={colors?.start ?? '#838383'} />
          <stop offset='100%' stopColor={colors?.end ?? 'white'} />
        </linearGradient>
      </defs>
      <path
        d={pathData}
        fill='none'
        stroke={`url(#${gradientId})`}
        strokeWidth={
          type === 'mini' ? CHART_CONSTANTS.PATH.STROKE_WIDTH.MINI : CHART_CONSTANTS.PATH.STROKE_WIDTH.DEFAULT
        }
        strokeLinecap='round'
      />
      <line
        x1={activePoint.x}
        y1={PADDING}
        x2={activePoint.x}
        y2={dimensions.height}
        stroke={token('colors.border.default.level-4')}
        strokeWidth={1}
        strokeDasharray='2 2'
      />
      <g>
        {activeIndex === data.length - 1 && (
          <circle
            cx={activePoint.x}
            cy={activePoint.y}
            r={POINT_SIZE}
            fill={colors?.end ?? 'white'}
            className='point-background'
          />
        )}
        <circle cx={activePoint.x} cy={activePoint.y} r={INNER_RADIUS} fill={colors?.end ?? 'white'} />
      </g>
      <style>
        {`
          @keyframes pointAnimation {
            0% { opacity: 0; }
            50% { opacity: 0.3; }
            100% { opacity: 0; }
          }

          .point-background {
            animation: pointAnimation 1s ease-in-out infinite;
          }
        `}
      </style>
    </svg>
  );
};

// 차트 상수
const CHART_CONSTANTS = {
  PADDING: {
    DEFAULT: 32,
    MINI: 8,
  },
  POINT: {
    SIZE: {
      DEFAULT: 13,
      MINI: 7,
    },
    INNER_RADIUS: {
      DEFAULT: 4.5,
      MINI: 2.5,
    },
    STROKE_WIDTH: 3,
  },
  PATH: {
    STROKE_WIDTH: {
      DEFAULT: 4,
      MINI: 2,
    },
  },
  TEXT_MARGIN: 5,
} as const;

// 차트 데이터 계산 hook
const useChartData = (
  data: number[],
  dimensions: { width: number; height: number },
  type: 'default' | 'mini',
  padding: number,
  textMargin: number,
) => {
  return useMemo(() => {
    if (!data?.length) return [];

    const innerWidth = Math.max(0, dimensions.width - padding * 2);
    const innerHeight = Math.max(0, dimensions.height - padding * 2 - (type === 'default' ? textMargin : 0));
    const xScale = innerWidth / Math.max(1, data.length - 1);

    const minVal = Math.min(...data);
    const maxVal = Math.max(...data);
    const isFlatLine = minVal === maxVal;
    const yRange = isFlatLine ? Math.max(Math.abs(maxVal) * 0.01, 0.01) : maxVal - minVal;
    const min = isFlatLine ? minVal - yRange / 2 : minVal;

    return data.map((point, i) => {
      const x = padding + i * xScale;
      const normalizedBalance = (point - min) / yRange;
      const y = padding + innerHeight * (1 - normalizedBalance);

      return {
        x,
        y: Math.max(padding, Math.min(dimensions.height - padding, y)),
      };
    });
  }, [data, dimensions, type, padding, textMargin]);
};
